import { LoadingOutlined, UploadOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Input,
  Row,
  Select,
  Upload,
  message,
  Typography,
} from "antd";
import Checkbox from "antd/lib/checkbox/Checkbox";
import { FC, useState, useEffect } from "react";

import { ICountry, IJobGrade, IModal } from "@types";
import CustomModal from "@/components/customModel";
import {
  ErrorServices,
  useFetchAllCompanyJobGradesQuery,
  useFetchAllCompanyJobFunctionsQuery,
  useFetchBenefitPlanTypesQuery,
  useFetchBusinessUnitsQuery,
  useFetchCompanyCountriesQuery,
  useUpdateBenefitsPlanMutation,
} from "@services";
import {
  ALL_COUNTRIES,
  ALL_GRADES_VALUE,
  showSuccessPopup,
  sortedByName,
} from "@utils";
import { useTypedSelector } from "@hooks";
import {
  getSelectedCompanyFromStorage,
  getTokenFromSharedStorage,
} from "@/services/cookies";
import customErrorMessage from "@/components/CustomErrorMessage";
import { formatNumberWithCommas } from "../AddBenefitsPlan";

const { Option } = Select;
const { Text } = Typography;
interface IAddBenefitsPlan extends IModal {
  selectedBenefitPlan: any | null;
  setSelectedBenefitPlan: React.Dispatch<React.SetStateAction<any | null>>;
}

interface IBenefitPlanForm {
  name: string;
  category: string | null;

  business_unit_ids: number[];
  job_function_id: number | null;
  sub_job_function_ids: number[];

  employee_type_category: string | null;

  grade: string[];
  is_all_grade: boolean;

  country_id: number[];
  city: string;

  benefit_entitlement: string;
  calculation_basis: string | null;

  benefit_value_or_cte: number | null;
  benefit_limit: number | null;

  do_not_show_in_offer: boolean;
  custom_detail_in_offer: boolean;
  offer_custom_detail: string;

  description: string;

  policy_manual: File | null;
}

const INITIAL_BENEFITS_PLAN: IBenefitPlanForm = {
  name: "",
  category: null,

  business_unit_ids: [],
  job_function_id: null,
  sub_job_function_ids: [],

  employee_type_category: null,

  grade: [],
  is_all_grade: false,

  country_id: [],
  city: "",

  benefit_entitlement: "",
  calculation_basis: null,

  benefit_value_or_cte: null,
  benefit_limit: null,

  do_not_show_in_offer: false,
  custom_detail_in_offer: false,
  offer_custom_detail: "",

  description: "",

  policy_manual: null,
};

const EditBenefitModal: FC<IAddBenefitsPlan> = ({
  isVisible,
  setIsVisible,
  selectedBenefitPlan,
}) => {
  const { company } = useTypedSelector(state => state.auth.user!);
  const { id: company_id } = company!;
  const [loading, setLoading] = useState(false);

  const [benefitPlan, setBenefitPlan] = useState<any>(INITIAL_BENEFITS_PLAN);

  const { data: planTypes, isLoading } = useFetchBenefitPlanTypesQuery();
  const [updatebenefitPlan, { isLoading: isUpdating }] =
    useUpdateBenefitsPlanMutation();

  const [gradeCountries, setGradeCountries] = useState<ICountry[]>([]);
  const { data: countriesData } = useFetchCompanyCountriesQuery({ company_id });
  const countries = countriesData?.data || [];

  const { data: jobGradesData } = useFetchAllCompanyJobGradesQuery({
    company_id,
  });
  const jobGrades = jobGradesData?.data || [];

  const { data: businessUnitsData } = useFetchBusinessUnitsQuery({ company_id });
  const businessUnits = businessUnitsData?.data || [];

  const {
    data: companyJobFunctionsData,
    isLoading: isLoadingCompanyJobFunctions,
  } = useFetchAllCompanyJobFunctionsQuery({
    companyId: company_id,
  });

  const companyJobFunctions = Array.isArray(companyJobFunctionsData?.data)
    ? companyJobFunctionsData.data
    : companyJobFunctionsData?.data?.results || [];

  const selectedCompanyJobFunction =
    companyJobFunctions.find(
      (jobFunction: any) =>
        jobFunction.id === benefitPlan?.job_function_id
    );

  const companySubJobFunctions =
    selectedCompanyJobFunction?.job_sub_functions ||
    selectedCompanyJobFunction?.sub_job_functions ||
    [];

  const isEmployeeTypeDisabled =
    benefitPlan?.job_function_id ||
    benefitPlan?.sub_job_function_ids?.length > 0;

  const isJobFunctionDisabled =
    !!benefitPlan?.employee_type_category;

  useEffect(() => {
    if (!selectedBenefitPlan || !jobGrades?.length) return;

    console.log("selected benefit", selectedBenefitPlan)

    const selectedGrades =
      selectedBenefitPlan?.grades?.map((grade: any) =>
        typeof grade === "string"
          ? grade
          : grade?.grade || grade?.name
      )?.filter(Boolean) || [];

    const countryIds =
      selectedBenefitPlan?.countries?.map((country: any) =>
        country?.id ??
        country?.country_id
      )?.filter((id: any) => id !== null && id !== undefined) || [];

    const businessUnitIds = (
      selectedBenefitPlan?.business_units || []
    )
      .map((businessUnit: any) => businessUnit.id)
      .filter(Boolean);

    const subJobFunctionIds = (
      selectedBenefitPlan?.sub_job_function ||
      selectedBenefitPlan?.sub_job_functions ||
      []
    )
      .map(
        (subJobFunction: any) =>
          subJobFunction?.id ??
          subJobFunction?.sub_job_function_id
      )
      .filter(
        (id: any) =>
          id !== null &&
          id !== undefined
      );

    const jobFunctionIds = (
      selectedBenefitPlan?.job_function ||
      selectedBenefitPlan?.job_functions ||
      []
    )
      .map(
        (jobFunction: any) =>
          jobFunction?.id ??
          jobFunction?.job_function_id
      )
      .filter(
        (id: any) =>
          id !== null &&
          id !== undefined
      );

    const jobFunctionId =
      jobFunctionIds.length > 0
        ? jobFunctionIds[0]
        : selectedBenefitPlan?.job_function_id ??
        null;

    const allAvailableGrades = Array.from(
      new Set(
        jobGrades
          ?.map((jobGrade: any) => jobGrade?.grade)
          ?.filter(Boolean)
      )
    );

    const isAllGrade =
      selectedBenefitPlan?.all_grades ??
      selectedBenefitPlan?.is_all_grade ??
      (
        selectedGrades.length > 0 &&
        selectedGrades.length === allAvailableGrades.length
      );

    const formattedBenefitPlan: IBenefitPlanForm = {
      name: selectedBenefitPlan?.name || "",
      category: selectedBenefitPlan?.category || null,

      business_unit_ids: businessUnitIds,
      job_function_id: jobFunctionId,
      sub_job_function_ids: subJobFunctionIds,

      employee_type_category:
        jobFunctionId || subJobFunctionIds.length > 0
          ? null
          : selectedBenefitPlan?.employee_type_category || null,

      grade: selectedGrades,
      is_all_grade: Boolean(isAllGrade),

      country_id: countryIds,
      city: selectedBenefitPlan?.city || "",

      benefit_entitlement:
        selectedBenefitPlan?.benefit_entitlement || "",

      calculation_basis:
        selectedBenefitPlan?.calculation_basis || null,

      benefit_value_or_cte:
        selectedBenefitPlan?.benefit_value_or_cte != null
          ? Number(selectedBenefitPlan.benefit_value_or_cte)
          : null,

      benefit_limit:
        selectedBenefitPlan?.benefit_limit != null
          ? Number(selectedBenefitPlan.benefit_limit)
          : null,

      do_not_show_in_offer:
        selectedBenefitPlan?.exclude_from_offer ?? false,

      custom_detail_in_offer:
        selectedBenefitPlan?.show_custom_detail ?? false,

      offer_custom_detail:
        selectedBenefitPlan?.custom_detail ?? "",

      description: selectedBenefitPlan?.description || "",

      policy_manual: null,
    };

    const selectedJobGrades = jobGrades.filter((jobGrade: any) =>
      selectedGrades.includes(jobGrade?.grade)
    );

    const mergedCountries =
      selectedJobGrades.flatMap(
        (jobGrade: any) => jobGrade?.countries || []
      ) || [];

    const uniqueCountries = mergedCountries.filter(
      (country: any, index: number, self: any[]) =>
        country &&
        index ===
        self.findIndex(
          existingCountry => existingCountry?.id === country?.id
        )
    );

    setGradeCountries(uniqueCountries);
    setBenefitPlan(formattedBenefitPlan);
  }, [selectedBenefitPlan, jobGradesData?.data]);

  // Handle input & checkbox changes
  const handleChange = (e: any) => {
    const { name, value, checked, type } = e.target;

    let updatedValue: any =
      type === "checkbox" ? checked : value;

    if (
      [
        "benefit_limit",
        "benefit_value_or_cte",
      ].includes(name)
    ) {
      updatedValue =
        value === ""
          ? null
          : Number(String(value).replace(/,/g, ""));
    }

    setBenefitPlan((prev: any) => ({
      ...prev,
      [name]: updatedValue,
    }));

    if (name === "is_all_grade" && checked) {
      setGradeCountries(countries);
    }
  };

  // Handle Select changes
  const handleSelectChange = (name: string, value: any) => {
    if (name === "job_function_id") {
      setBenefitPlan((prev: any) => ({
        ...prev,
        job_function_id: value,
        sub_job_function_ids: [],
        employee_type_category: null,
      }));
      return;
    }

    if (name === "sub_job_function_ids") {
      setBenefitPlan((prev: any) => ({
        ...prev,
        sub_job_function_ids: value,
        employee_type_category: null,
      }));
      return;
    }

    if (name === "employee_type_category") {
      setBenefitPlan((prev: any) => ({
        ...prev,
        employee_type_category: value,
        job_function_id: null,
        sub_job_function_ids: [],
      }));
      return;
    }

    setBenefitPlan((prev: any) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleGradeChange = (selectedGrades: string[]) => {
    const selectedJobGrades = jobGrades.filter(jg =>
      selectedGrades?.includes(jg.grade)
    );
    const mergedCountries =
      selectedJobGrades?.flatMap(jg => jg.countries || []) || [];
    const uniqueCountries = mergedCountries.filter(
      (c, idx, self) => c && idx === self.findIndex(sc => sc.id === c.id)
    );
    setGradeCountries(uniqueCountries);
    setBenefitPlan(prev => ({
      ...prev,
      grade: selectedGrades,
      country_id: [], // reset selected country if grades change
      is_global: false,
    }));
  };
  const handleGlobalChange = (checked: boolean) => {
    const allCountryIds =
      gradeCountries.map((country: any) => country.id);

    setBenefitPlan((prev: any) => ({
      ...prev,
      country_id: checked ? allCountryIds : [],
    }));
  };


  const uploadPolicy = async (): Promise<boolean> => {
    if (!benefitPlan?.policy_manual) return false; // no file = allow submit
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("policy_manual", benefitPlan.policy_manual);

      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}/company/${company_id}/upload-policy/`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${getTokenFromSharedStorage()?.access}`,
            Accept: "application/json",
          },
          body: formData,
        }
      );
      setLoading(false);
      if (!res.ok) {
        throw new Error("Upload failed");
      }

      const data = await res.json();
      return data;
    } catch (error) {
      setLoading(false);
      return false;
    }
  };

  // Upload handler
  const handleUpload = (file: any) => {
    setBenefitPlan(prev => ({ ...prev, policy_manual: file }));
    return false; // prevent auto upload
  };

  // Validation
  const isValid = () => {
    const hasGrades =
      benefitPlan?.is_all_grade ||
      (benefitPlan?.grade &&
        benefitPlan.grade.length > 0);

    const hasCountry =
      Array.isArray(benefitPlan?.country_id) &&
      benefitPlan.country_id.length > 0;

    const hasCustomDetail =
      !benefitPlan?.custom_detail_in_offer ||
      !!benefitPlan?.offer_custom_detail;

    const validationState = {
      name: !!benefitPlan?.name,
      category: !!benefitPlan?.category,
      // businessUnits: benefitPlan?.business_unit_ids?.length > 0,
      jobFunction: !!benefitPlan?.job_function_id,
      subJobFunctions: benefitPlan?.sub_job_function_ids,
      grades: hasGrades,
      countries: hasCountry,
      city: !!benefitPlan?.city,
      benefitEntitlement: !!benefitPlan?.benefit_entitlement,
      calculationBasis: !!benefitPlan?.calculation_basis,
      employerCostValue:
        benefitPlan?.benefit_value_or_cte !== null &&
        benefitPlan?.benefit_value_or_cte !== undefined &&
        benefitPlan?.benefit_value_or_cte !== "",
      benefitLimit:
        benefitPlan?.benefit_limit !== null &&
        benefitPlan?.benefit_limit !== undefined &&
        benefitPlan?.benefit_limit !== "",
      // description: !!benefitPlan?.description,
      customDetail: hasCustomDetail,
      employeeTypeCategory: benefitPlan?.employee_type_category,
    };

    console.log("Edit Benefit Validation:", validationState);

    return Object.values(validationState)
      .filter(value => typeof value === "boolean")
      .every(Boolean);
  };

  const handleSubmitUpdate = async () => {
    try {
      // 1️⃣ Upload policy first
      const isUploaded: any = await uploadPolicy();
      // if (
      //   !selectedBenefitPlan?.policy_manual_url &&
      //   !benefitPlan?.policy_manual
      // ) {
      //   customErrorMessage("Policy is required. Benefit plan not updated.");
      //   return;
      // }
      const payload = {
        name: benefitPlan?.name,

        category: benefitPlan?.category,

        employee_type_category:
          benefitPlan?.job_function_id ||
            benefitPlan?.sub_job_function_ids?.length > 0
            ? null
            : benefitPlan?.employee_type_category || null,

        benefit_entitlement:
          benefitPlan?.benefit_entitlement || "",

        calculation_basis:
          benefitPlan?.calculation_basis || null,

        benefit_value_or_cte:
          benefitPlan?.benefit_value_or_cte !== null &&
            benefitPlan?.benefit_value_or_cte !== undefined &&
            benefitPlan?.benefit_value_or_cte !== ""
            ? Number(benefitPlan.benefit_value_or_cte)
            : null,

        benefit_limit:
          benefitPlan?.benefit_limit !== null &&
            benefitPlan?.benefit_limit !== undefined &&
            benefitPlan?.benefit_limit !== ""
            ? Number(benefitPlan.benefit_limit)
            : null,

        exclude_from_offer:
          benefitPlan?.do_not_show_in_offer,

        show_custom_detail:
          benefitPlan?.custom_detail_in_offer,

        description:
          benefitPlan?.description,

        country_ids:
          benefitPlan?.country_id || [],

        city:
          benefitPlan?.city,

        grade_ids:
          benefitPlan?.is_all_grade
            ? jobGrades.map((jg: any) => jg.id)
            : jobGrades
              .filter((jg: any) =>
                benefitPlan?.grade?.includes(jg.grade)
              )
              .map((jg: any) => jg.id),

        all_grades:
          benefitPlan?.is_all_grade,

        business_unit_ids:
          benefitPlan?.business_unit_ids || [],

        custom_detail:
          benefitPlan?.custom_detail_in_offer
            ? benefitPlan?.offer_custom_detail
            : null,

        job_function_id:
          benefitPlan?.job_function_id,

        sub_job_function_ids:
          benefitPlan?.sub_job_function_ids?.length
            ? benefitPlan?.sub_job_function_ids
            : null,
      };

      console.log("UPDATE PAYLOAD:", payload);

      // if (
      //   selectedBenefitPlan &&
      //   benefitPlan?.name &&
      //   benefitPlan?.city &&
      //   benefitPlan?.description &&
      //   benefitPlan?.category
      // ) {
      await updatebenefitPlan({
        company_id,
        id: selectedBenefitPlan?.id,
        body: payload,
      }).unwrap();
      showSuccessPopup({
        title: "Benefit Plan Updated",
        desc: "You have successfully updated the benefits plan.",
        role: "client",
      });
      // }

      setIsVisible(false);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      ErrorServices(error);
    }
  };
  return (
    <CustomModal
      width="w-7/12"
      title={`Update Benefit Plan`}
      isVisible={isVisible}
      height="80vh"
      footer={
        <>
          <button
            className="bg-revampOrange text-white font-medium text-lg py-2 px-14 rounded-md"
            onClick={() => setIsVisible(false)}
          >
            {" "}
            <span> Cancel</span>
          </button>
          <button
            className={`bg-revampBlue text-white font-medium text-lg py-2 px-14 rounded-md ${!isValid() && "opacity-50 cursor-auto"}`}
            type="primary"
            disabled={!isValid()}
            onClick={handleSubmitUpdate}
          >
            <span>
              {isUpdating || loading ? <LoadingOutlined /> : "Update"}
            </span>
          </button>
        </>
      }
      onClose={() => setIsVisible(false)}
    >
      <Row gutter={16}>
        <Col span={12}>
          <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
            Name
          </label>
          <Input
            className={`borderless-input w-full shadow-sm  font-semibold text-sm bg-white px-3 py-3 rounded-md`}
            size="large"
            name="name"
            placeholder="Name of Benefit Plan"
            value={benefitPlan?.name}
            onChange={handleChange}
          />
        </Col>
        {/* <Col span={12}>
          <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
            Business Unit
          </label>

          <Select
            className="custom-select"
            size="large"
            mode="multiple"
            maxTagCount="responsive"
            maxTagPlaceholder={omittedValues =>
              `+${omittedValues.length} more`
            }
            placeholder="Select Business Units"
            value={benefitPlan?.business_unit_ids || undefined}
            onChange={values =>
              handleSelectChange(
                "business_unit_ids",
                values
              )
            }
          >
            {businessUnits.map((bu: any) => (
              <Option key={bu.id} value={bu.id}>
                {bu.name}
              </Option>
            ))}
          </Select>
        </Col> */}
      </Row>

      <Row gutter={16} style={{ marginTop: 16 }}>
        <Col span={12}>
          <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
            Company Function
          </label>

          <Select
            className="custom-select"
            size="large"
            showSearch
            optionFilterProp="children"
            loading={isLoadingCompanyJobFunctions}
            placeholder="Select Company Function"
            value={benefitPlan?.job_function_id || undefined}
            disabled={isJobFunctionDisabled}
            onChange={value =>
              handleSelectChange(
                "job_function_id",
                value
              )
            }
          >
            {sortedByName(companyJobFunctions)?.map(
              (item: any) => (
                <Option
                  key={item.id}
                  value={item.id}
                >
                  {item.name}
                </Option>
              )
            )}
          </Select>
        </Col>

        <Col span={12}>
          <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
            Company Sub Function
          </label>

          <Select
            className="custom-select"
            size="large"
            mode="multiple"
            showSearch
            optionFilterProp="children"
            maxTagCount="responsive"
            maxTagPlaceholder={o =>
              `+${o.length} more`
            }
            disabled={
              !benefitPlan?.job_function_id
            }
            placeholder="Select Company Sub Function"
            value={
              benefitPlan?.sub_job_function_ids ||
              undefined
            }
            onChange={values =>
              handleSelectChange(
                "sub_job_function_ids",
                values
              )
            }
          >
            {sortedByName(
              companySubJobFunctions
            )?.map((item: any) => (
              <Option
                key={item.id}
                value={item.id}
              >
                {item.name}
              </Option>
            ))}
          </Select>
        </Col>
      </Row>


      <>
        <Row gutter={16} style={{ marginTop: 16 }}>
          <Col span={12}>
            <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
              Employee Type Category
            </label>

            <Select
              className="custom-select"
              size="large"
              disabled={isEmployeeTypeDisabled}
              value={
                benefitPlan?.employee_type_category
              }
              onChange={value =>
                handleSelectChange(
                  "employee_type_category",
                  value
                )
              }
            >
              <Option value="permanent">
                Permanent
              </Option>

              <Option value="contract">
                Contract
              </Option>

              <Option value="temporary">
                Temporary
              </Option>

              <Option value="intern">
                Intern
              </Option>
            </Select>
          </Col>

          <Col span={12}>
            <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
              Grades
            </label>
            <Select
              className="custom-select"
              size="large"
              mode="multiple"
              maxTagCount="responsive"
              maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} more`}
              placeholder="Grades"
              disabled={benefitPlan?.is_all_grade}
              value={benefitPlan?.grade?.length ? benefitPlan.grade : undefined}
              onChange={values => {
                if (values?.includes(ALL_GRADES_VALUE)) {
                  setBenefitPlan(prev => ({
                    ...prev,
                    country_id: [], // reset selected country if grades change
                    is_global: false,
                  }));
                  handleGradeChange(
                    Array.from(new Set(jobGrades?.map((jg: any) => jg?.grade))) ||
                    []
                  );
                } else {
                  handleGradeChange(values);
                }
              }}
            >
              {benefitPlan?.grade?.length !==
                [...new Set(jobGrades?.map(jgd => jgd?.grade)?.filter(Boolean))]
                  ?.length && (
                  <Option key={ALL_GRADES_VALUE} value={ALL_GRADES_VALUE}>
                    All Grades
                  </Option>
                )}
              {[
                ...new Set(jobGrades?.map(jgd => jgd?.grade)?.filter(Boolean)),
              ].map(jg => (
                <Option key={jg} value={jg}>
                  {jg}
                </Option>
              ))}
            </Select>
            <Row style={{ marginTop: 16 }}></Row>
          </Col>
        </Row>
      </>

      {benefitPlan?.benefit_type === "non_cash" && (
        <>
          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col span={12}>
              <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
                Benefit Value
              </label>
              <Input
                className={`borderless-input w-full shadow-sm my-2 font-semibold text-sm bg-white px-3 py-3 rounded-md`}
                size="large"
                name="value"
                placeholder="Benefit Value"
                value={formatNumberWithCommas(benefitPlan?.value)}
                onChange={handleChange}
              />
            </Col>
          </Row>
          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col span={12}>
              <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
                Cost to Employer
              </label>
              <Input
                className={`borderless-input w-full shadow-sm my-2 font-semibold text-sm bg-white px-3 py-3 rounded-md`}
                size="large"
                name="employer_cost"
                placeholder="Cost to Employer"
                value={formatNumberWithCommas(benefitPlan?.employer_cost)}
                onChange={handleChange}
              />
            </Col>
          </Row>
        </>
      )}
      <Row gutter={16} style={{ marginTop: 16 }}>

        <Col span={12}>
          <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
            Country
          </label>

          <Select
            className="custom-select"
            size="large"
            mode="multiple"
            maxTagCount="responsive"
            maxTagPlaceholder={o => `+${o.length} more`}
            disabled={gradeCountries?.length === 0}
            placeholder="Grades determine available countries"
            value={benefitPlan?.country_id || undefined}
            onChange={values => {
              if (values?.includes(ALL_COUNTRIES)) {
                handleGlobalChange(true);
              } else {
                handleSelectChange("country_id", values);
              }
            }}
          >
            {benefitPlan?.country_id?.length !==
              gradeCountries?.length && (
                <Option
                  key={ALL_COUNTRIES}
                  value={ALL_COUNTRIES}
                >
                  All Countries
                </Option>
              )}

            {sortedByName(gradeCountries)?.map(
              (country: any) => (
                <Option
                  key={country.id}
                  value={country.id}
                >
                  {country.name}
                </Option>
              )
            )}
          </Select>
        </Col>

        <Col span={12}>
          <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
            City
          </label>
          <Input
            className={`borderless-input w-full shadow-sm  font-semibold text-sm bg-white px-3 py-3 rounded-md`}
            size="large"
            name="city"
            placeholder="City"
            value={benefitPlan?.city}
            onChange={handleChange}
          />
        </Col>
      </Row>
      <Row gutter={16} style={{ marginTop: 16 }}>
        <Col span={12}>
          <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
            Benefit Category
          </label>

          <Select
            className="custom-select"
            size="large"
            placeholder="Select Benefit Category"
            value={benefitPlan?.category}
            onChange={value =>
              handleSelectChange("category", value)
            }
          >
            <Option value="health">
              Health
            </Option>

            <Option value="retirement">
              Retirement
            </Option>

            <Option value="statutory">
              Statutory
            </Option>

            <Option value="wellness">
              Wellness
            </Option>

            <Option value="financial">
              Financial
            </Option>

            <Option value="lifestyle">
              Lifestyle
            </Option>

            <Option value="statutory_time_off">
              Statutory – Time Off
            </Option>

            <Option value="statutory_provisions">
              Statutory – Provisions
            </Option>

            <Option value="others">
              Others
            </Option>
          </Select>
        </Col>

        <Col span={12}>
          <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
            Benefit Entitlement
          </label>

          <Input.TextArea
            className="w-full resize shadow-sm font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md"
            name="benefit_entitlement"
            placeholder="Enter Benefit Entitlement"
            value={benefitPlan?.benefit_entitlement}
            onChange={handleChange}
            autoSize={{
              minRows: 3,
              maxRows: 5,
            }}
          />
        </Col>
        {/* <Col span={14} className="mt-2">
          <Checkbox
            name="include_in_offer_detail"
            checked={benefitPlan?.include_in_offer_detail}
            onChange={handleChange}
          >
            Include in Offer Detail
          </Checkbox>

          <br />

          <Checkbox
            className="mt-1"
            name="include_in_total_remuneration"
            checked={benefitPlan?.include_in_total_remuneration}
            onChange={handleChange}
          >
            Include in Total Remuneration
          </Checkbox>

        </Col> */}
      </Row>


      <Row gutter={16} style={{ marginTop: 16 }}>
        <Col span={12}>
          <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
            Calculation Basis
          </label>

          <Select
            className="custom-select"
            size="large"
            placeholder="Calculation Basis"
            value={benefitPlan?.calculation_basis}
            onChange={value =>
              handleSelectChange(
                "calculation_basis",
                value
              )
            }
          >
            <Option value="percent_basic">
              % of Annual Basic Salary
            </Option>

            <Option value="percent_guaranteed">
              % of Annual Guaranteed Cash
            </Option>

            <Option value="percent_actual">
              % of Actual
            </Option>

            <Option value="flat">
              Flat Amount
            </Option>
            <Option value="multiple_of_weekly_basic">
              multiple_of_weekly_basic
            </Option>
          </Select>
        </Col>

        <Col span={12}>
          <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
            Benefit Value / Cost-to-Employer
          </label>

          <Input
            type="number"
            className="borderless-input w-full shadow-sm  font-semibold text-sm bg-white px-3 py-3 rounded-md"
            size="large"
            name="benefit_value_or_cte"
            placeholder="Benefit Value / Cost-to-Employer"
            value={benefitPlan?.benefit_value_or_cte}
            onChange={handleChange}
          />
        </Col>




      </Row>


      <Row gutter={16} style={{ marginTop: 16 }}>
        <Col span={12}>
          <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
            Benefit Limit
          </label>

          <Input
            type="number"
            className="borderless-input w-full shadow-sm font-semibold text-sm bg-white px-3 py-3 rounded-md"
            size="large"
            name="benefit_limit"
            placeholder="Benefit Limit"
            value={benefitPlan?.benefit_limit}
            onChange={handleChange}
          />
        </Col>
      </Row>

      <Row gutter={16} style={{ marginTop: 20 }}>
        <Col span={12}>
          <div>
            <Checkbox
              name="do_not_show_in_offer"
              checked={benefitPlan?.do_not_show_in_offer}
              onChange={handleChange}
            >
              Do not show this Benefit in the Offer
            </Checkbox>
          </div>

          <div style={{ marginTop: 12 }}>
            <Checkbox
              name="custom_detail_in_offer"
              checked={benefitPlan?.custom_detail_in_offer}
              onChange={handleChange}
            >
              Custom detail to show in Offer
            </Checkbox>
          </div>
        </Col>
      </Row>

      {
        benefitPlan?.custom_detail_in_offer && (
          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col span={12}>
              <label className="font-medium text-revampBlue text-lg leading-10 mt-4">
                Custom Offer Detail
              </label>

              <Input.TextArea
                className="w-full resize shadow-sm my-2 font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md"
                name="offer_custom_detail"
                placeholder="Enter the custom detail to display in the offer"
                value={benefitPlan?.offer_custom_detail}
                onChange={handleChange}
              />

              <Text type="secondary">
                Benefit Value/Cost-to-Employer and Benefit Limit will not be displayed.
              </Text>
            </Col>
          </Row>
        )
      }

      {/* <Row gutter={16} style={{ marginTop: 16 }}>
        <Col span={24}>
          <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
            Description
          </label>

          <Input.TextArea
            className="w-full resize shadow-sm my-2 font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md"
            name="description"
            placeholder="Describe the benefit plan..."
            value={benefitPlan?.description}
            onChange={handleChange}
          />
        </Col>
      </Row> */}

      <Row gutter={16} style={{ marginTop: 16 }}>
        <Col span={12}>
          <>
            {selectedBenefitPlan?.policy_manual_url && (
              <Button className="mb-4" type="link">
                <a
                  href={selectedBenefitPlan?.policy_manual_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View Existing Policy Manual
                </a>
              </Button>
            )}

            <Card bordered={false} style={{ background: "#fafafa" }}>
              <Text strong>Policy Manual</Text>
              <Text
                type="secondary"
                style={{ display: "block", marginBottom: 8 }}
              >
                Upload PDF or DOC/DOCX file
              </Text>

              <Upload
                beforeUpload={file => {
                  const fileExtension = file.name
                    .split(".")
                    .pop()
                    ?.toLowerCase();
                  const allowedExtensions = ["pdf", "doc", "docx"];

                  if (
                    !fileExtension ||
                    !allowedExtensions?.includes(fileExtension)
                  ) {
                    customErrorMessage(
                      "You can only upload PDF or DOC/DOCX files!"
                    );
                    return Upload.LIST_IGNORE;
                  }

                  handleUpload(file);
                  return false; // stop auto upload but keep valid file
                }}
                onRemove={() => {
                  setBenefitPlan(prev => ({ ...prev, policy_manual: null }));
                }}
                maxCount={1}
              >
                <button className="bg-themeBlue text-white font-medium text-lg py-1 px-6 rounded-md">
                  <UploadOutlined /> Upload File
                </button>
              </Upload>
            </Card>
          </>
        </Col>
      </Row>
    </CustomModal>
  );
};

export default EditBenefitModal;
